import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Rocket, Target, ArrowLeft } from 'lucide-react';

// SERVICIO que llama a tu backend
import growthStageService from '../../services/AGTUps-product-services/growthStageService';

// Importamos el botón especial que creaste
import ViewDetailedButton from './ViewDetailedButton';

/**
 * Styled Button for back navigation with wallpaper mode support
 */
const StyledBackButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

/**
 * Estilos para el contenedor principal (Paper)
 */
const StyledPaper = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    boxShadow: 'none',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : undefined,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${theme.palette.divider}`
  };
});

/**
 * Componente principal "Stage", que muestra:
 *  - Vista "overview" de la etapa de crecimiento
 *  - Vista "detailed" con el análisis detallado
 * dentro del mismo componente (sin usar modal ni navigate).
 */
const Stage = () => {
  // 1. Controlamos qué vista mostrar: 'overview' o 'detailed'
  const [view, setView] = useState('overview');

  // 2. Guardar aquí el análisis detallado cuando lo traigamos del backend
  const [detailedAnalysis, setDetailedAnalysis] = useState(null);

  // 3. Estados de la info básica, spinner, y error
  const [stageData, setStageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Definimos las etapas y su posición para la barra de progreso
  const growthStages = {
    'Pre-Seed': 0,
    'Seed': 1,
    'Early': 2,
    'Scale-Up': 3,
    'Expansion': 4
  };

  /**
   * useEffect para obtener la info inicial de la etapa
   * cuando se monta el componente.
   */
  useEffect(() => {
    const fetchStageData = async () => {
      try {
        setLoading(true);
        const response = await growthStageService.analyzeGrowthStage();
        if (response.success) {
          // Suponemos que growthStageService.formatStageData formatea la respuesta
          setStageData(growthStageService.formatStageData(response.data));
        } else {
          setError('No se pudo obtener la información de la etapa de crecimiento.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStageData();
  }, []);

  /**
   * Llamada al servicio que obtiene el análisis detallado.
   * Luego cambiamos la "vista" a 'detailed'.
   */
  const handleViewDetailedAnalysis = async () => {
    try {
      setLoading(true);
      const response = await growthStageService.getDetailedAnalysis();
      if (response.success) {
        // Formateamos la data detallada (si tu servicio lo requiere)
        const formatted = growthStageService.formatDetailedAnalysis(response.data);

        // Guardamos en el estado local
        setDetailedAnalysis(formatted);

        // Cambiamos la vista a "detailed"
        setView('detailed');
      } else {
        setError('No se pudo obtener el análisis detallado.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Determinamos la etapa actual
  const activeStage = stageData?.currentStage || 'Pre-Seed';

  /**
   * Render condicional:
   * 1) Si está "loading": Spinner
   * 2) Si hay "error": Alert
   * 3) Si `view === 'detailed'` => análisis detallado
   * 4) Por defecto => overview
   */

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Vista "detailed"
  if (view === 'detailed' && detailedAnalysis) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          {/* Botón para volver a la vista overview */}
          <StyledBackButton
            variant="text"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => setView('overview')}
          >
            Volver
          </StyledBackButton>

          <StyledPaper sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Análisis Detallado
            </Typography>

            <Typography variant="body1" paragraph>
              <strong>Etapa actual:</strong> {detailedAnalysis.currentStage}
            </Typography>

            <Typography variant="body1" paragraph>
              <strong>Resumen:</strong> {detailedAnalysis.summary}
            </Typography>

            {/* Ejemplo: mostrar recomendaciones inmediatas */}
            {detailedAnalysis.recommendations?.immediate?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Acciones Inmediatas:</Typography>
                <ul>
                  {detailedAnalysis.recommendations.immediate.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </Box>
            )}

            {/* Ejemplo: mostrar oportunidades */}
            {detailedAnalysis.opportunities?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Oportunidades:</Typography>
                <ul>
                  {detailedAnalysis.opportunities.map((opp, idx) => (
                    <li key={idx}>{opp}</li>
                  ))}
                </ul>
              </Box>
            )}

            {/* Y así sucesivamente para "risks", "nextStage", etc. */}
          </StyledPaper>
        </Box>
      </Container>
    );
  }

  // Vista "overview" por defecto
  return (
    <Container maxWidth="lg">
      <StyledPaper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Current Growth Stage
            </Typography>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {activeStage}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<Rocket size={14} />}
                label="High Growth"
                color="success"
              />
              <Chip
                icon={<Target size={14} />}
                label="Series A Ready"
                color="primary"
              />
            </Box>
          </Box>

          {/* Botón para ver análisis detallado: importado desde ViewDetailedButton.js */}
          <ViewDetailedButton onClick={handleViewDetailedAnalysis} />
        </Box>

        {/* Barra de progreso que muestra la posición en las 5 etapas */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 1 }}>
            {Object.keys(growthStages).map((stage) => (
              <Typography
                key={stage}
                variant="caption"
                sx={(theme) => ({
                  fontWeight: stage === activeStage ? 600 : 400,
                  color: stage === activeStage
                    ? (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE')
                    : 'text.secondary'
                })}
              >
                {stage}
              </Typography>
            ))}
          </Box>

          <LinearProgress
            variant="determinate"
            value={
              ((growthStages[activeStage] + 1) /
               Object.keys(growthStages).length) * 100
            }
            sx={(theme) => ({
              height: 6,
              borderRadius: 3,
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE'
              }
            })}
          />
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default Stage;
